<template>
  <div
    v-if="isAdmin && message.role != 'system'"
    :class="message.role === 'user' ? 'flex-end' : 'flex-start'"
    style="margin-bottom: 0.5rem"
  >
    <button class="btn-icon tooltiped" @click="editing = !editing">
      <span class="tooltiptext" style="width: max-content">Edit</span>
      <font-awesome-icon icon="pen" class="black-icon" />
    </button>
    <button class="btn-icon" v-if="isAdmin" @click="emit('deleteMessage', message.id)">
      <font-awesome-icon icon="trash" class="black-icon" />
    </button>
  </div>
  <div :class="['message', messageTypeClasses[message.role], { editing: editing }]">
    <div v-if="editing" style="width: 100%">
      <textarea
        ref="userInputArea"
        v-model="userInput"
        placeholder="Type a message..."
        class="message-input"
        :class="message.role === 'user' ? 'message-input-user' : 'message-input'"
      ></textarea>
      <div style="display: flex; justify-content: flex-end">
        <button class="btn btn-primary" @click="saveMessage">Save</button>
      </div>
    </div>

    <div v-else>
      <div v-if="message.role == 'tool'">
        <div v-if="isAdmin">
          <div>Tool call response:</div>
          <div v-html="displayableMessage(message)" class="tool-message"></div>
        </div>
      </div>
      <div
        v-else
        class="ai-message-content"
        :class="isError ? 'error-message' : ''"
        v-html="displayableMessage(message)"
        :id="`message-${message.id}`"
      ></div>
      <div v-if="isAdmin && message.tool_calls">
        Assistant tool call:
        <div v-for="tool_call in message.tool_calls" :key="tool_call.id" class="tool-message">
          <div><span style="font-weight: bold">Function called: </span>{{ tool_call.function.name }}</div>
          <div><span style="font-weight: bold">Arguments: </span>{{ tool_call.function.arguments }}</div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="message.role == 'assistant'" class="flex-start" style="margin-bottom: 0.5rem">
    <button class="btn-icon" @click="rateMessage(true)">
      <font-awesome-icon icon="thumbs-up" class="black-icon" :class="{ 'green-icon': props.rating?.rating === true }" />
    </button>
    <button class="btn-icon" @click="rateMessage(false)">
      <font-awesome-icon
        icon="thumbs-down"
        class="black-icon"
        :class="{ 'green-icon': props.rating?.rating === false }"
      />
    </button>
    <button class="btn-icon" @click="emit('regen', props.message.id)">
      <font-awesome-icon icon="arrows-rotate" class="black-icon" />
    </button>
  </div>
  <div v-if="isError" class="flex-start" style="margin-bottom: 0.5rem">
    <button class="btn-icon" @click="emit('regen', '')">
      <font-awesome-icon icon="arrows-rotate" class="" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { userSessionStore } from "@/store/user_session";
import { ChatMessage, Rating } from "@/types/copilot";
import { useTextareaAutosize } from "@vueuse/core";
import { marked } from "marked";
import { onMounted, ref, watch } from "vue";

interface Props {
  message: ChatMessage;
  index: number;
  isAdmin: boolean;
  isError?: boolean;
  rating?: Rating;
}
const props = defineProps<Props>();
const user_session = userSessionStore();

const emit = defineEmits<{
  (e: "deleteMessage", id: string): void;
  (e: "editMessage", payload: ChatMessage): void;
  (e: "rate", payload: Rating): void;
  (e: "regen", id: string): void;
  (e: "abort"): void;
}>();
const { textarea: userInputArea, input: userInput } = useTextareaAutosize();
const messageTypeClasses = {
  user: "user-message",
  assistant: "ai-message",
  system: "ai-message",
  tool: "ai-message",
};
const editing = ref<boolean>(false);
const displayableMessage = (message: ChatMessage) => {
  let content = message.content.split("<ems-context>")[0];
  return marked.parse(content);
};

const rateMessage = (rating: boolean) => {
  const newRating = rating == props.rating?.rating ? undefined : rating;
  const payload: Rating = {
    id: props.message.id,
    rating: newRating,
    user_id: Number(user_session.logged_user.UserId),
  };
  emit("rate", payload);
};

const saveMessage = () => {
  editing.value = false;
  emit("editMessage", { ...props.message, content: userInput.value });
};
onMounted(() => {
  userInput.value = props.message.content;
});
watch(
  () => props.message,
  (newMessage) => {
    userInput.value = newMessage.content;
    editing.value = false;
  }
);
</script>

<style lang="css" scoped>
.message {
  margin-bottom: 15px;
}

.user-message {
  align-self: flex-end;
  background-color: var(--green-main-color);
  color: white;
  padding: 10px;
  border-radius: 15px;
  max-width: 80%;
  word-wrap: break-word;
  text-align: left;
}

.ai-message {
  align-self: flex-start;
  color: #333;
  max-width: 80%;
  word-wrap: break-word;
  text-align: left;
}
.error-message {
  color: var(--red-danger-color);
  font-size: 1.2em;
  font-weight: bold;
}
.tool-message {
  margin-top: 0.5rem;
  background-color: var(--grey-95);
  padding: 10px;
  border-radius: 15px;
}

.message-input {
  width: -webkit-fill-available;
  padding: 10px;
  border-radius: 15px;
  border: 1px solid #ddd;
  font-size: 16px;
  resize: none;
  overflow: auto;
  scrollbar-width: none;
  height: auto;
}
.message-input-user {
  background-color: var(--green-main-color);
  color: white;
}
.message-input-user::placeholder {
  color: var(--grey-80);
}

.flex-end {
  align-self: flex-end;
}
.flex-start {
  align-self: flex-start;
}
.editing {
  width: 100%;
  padding: 0px;
  background-color: transparent;
}
.green-icon {
  color: var(--green-main-color);
}
</style>
<style>
.ai-message-content > p {
  margin: 0;
}
</style>
